import React, { useState, useCallback, useEffect, useRef, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import HomePage from './components/HomePage';
import NotFoundPage from './components/NotFoundPage';
import Footer from './components/Footer';
import LoadingSpinner from './components/LoadingSpinner';
import ErrorMessage from './components/ErrorMessage';
import ScrollToTop from './components/ScrollToTop';
import AnalyticsRouteTracker from './components/AnalyticsRouteTracker';
import CookieConsent from './components/CookieConsent';
// eslint-disable-next-line no-unused-vars -- kept for the disabled auto-prompt below
import SupportModal from './components/SupportModal';
import LiveDebateBar from './components/LiveDebateBar';
import './App.css';

const PetitionPage         = lazy(() => import('./components/PetitionPage'));
const ComparePage        = lazy(() => import('./components/ComparePage'));
const OpenPetitionsPage    = lazy(() => import('./components/OpenPetitionsPage'));
const ClosedPetitionsPage  = lazy(() => import('./components/ClosedPetitionsPage'));
const LocalPetitionsPage   = lazy(() => import('./components/LocalPetitionsPage'));
const RejectedPetitionsPage= lazy(() => import('./components/RejectedPetitionsPage'));
const TrendingPetitionsPage= lazy(() => import('./components/TrendingPetitionsPage'));
const LatestPetitionsPage  = lazy(() => import('./components/LatestPetitionsPage'));
const AboutPage            = lazy(() => import('./components/AboutPage'));
const FAQPage              = lazy(() => import('./components/FAQPage'));
const AwaitingResponsePage = lazy(() => import('./components/AwaitingResponsePage'));
const DebateScheduledPage  = lazy(() => import('./components/DebateScheduledPage'));
const NearMilestonesPage   = lazy(() => import('./components/NearMilestonesPage'));
const PrivacyPage          = lazy(() => import('./components/PrivacyPage'));
const TermsPage            = lazy(() => import('./components/TermsPage'));
const StatsPage            = lazy(() => import('./components/StatsPage'));
const ChangelogPage        = lazy(() => import('./components/ChangelogPage'));
const FeaturesPage         = lazy(() => import('./components/FeaturesPage'));
const ContactPage          = lazy(() => import('./components/ContactPage'));
const InsightsPage         = lazy(() => import('./components/InsightsPage'));
const InsightArticlePage   = lazy(() => import('./components/InsightArticlePage'));

const AppContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #0f0c29 0%, #302b63 50%, #24243e 100%);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  display: flex;
  flex-direction: column;
`;

const Container = styled.div`
  width: 100%;
  margin: 0 auto;
  border-radius: 20px;
  backdrop-filter: blur(10px);
  min-height: 80vh;
  flex: 1;
`;

const NotificationContainer = styled.div`
  position: fixed;
  bottom: 22px;
  right: 22px;
  z-index: 1000;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;
  pointer-events: none;

  > * { pointer-events: auto; }

  /* Bottom-right keeps it clear of the floating section nav on the right edge
     and of the header, and matches where people expect transient toasts. */
  @media (max-width: 640px) {
    left: 12px;
    right: 12px;
    bottom: 12px;
    align-items: stretch;
  }
`;

const Notification = styled.div`
  display: flex;
  align-items: center;
  gap: 9px;
  background: rgba(22, 22, 43, 0.92);
  backdrop-filter: blur(12px);
  color: #fff;
  padding: 11px 16px 11px 13px;
  border-radius: 12px;
  border: 1px solid rgba(16, 185, 129, 0.4);
  box-shadow: 0 8px 28px rgba(0, 0, 0, 0.4);
  max-width: 300px;
  font-size: 0.88em;
  font-weight: 600;
  cursor: pointer;
  animation: slideInRight 0.28s cubic-bezier(0.16, 1, 0.3, 1);

  /* Green dot carries the "live" meaning, so the panel itself can stay dark
     and sit on the site's palette instead of a flat mid-green block. */
  &::before {
    content: '';
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #10b981;
    box-shadow: 0 0 0 4px rgba(16, 185, 129, 0.18);
    flex-shrink: 0;
  }

  @keyframes slideInRight {
    from { transform: translateX(110%); opacity: 0; }
    to   { transform: translateX(0); opacity: 1; }
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

// ============ ENHANCED ANALYTICS TRACKER COMPONENT ============
const EnhancedAnalyticsTracker = () => {
  const location = useLocation();
  const pageStartTime = useRef(Date.now());
  const maxScrollDepthRef = useRef(0);
  const clickCountRef = useRef(0);
  const scrollTimer = useRef(null);

  // Track page exit & petition page views
  useEffect(() => {
    const currentPath = location.pathname;
    pageStartTime.current = Date.now();
    maxScrollDepthRef.current = 0;
    clickCountRef.current = 0;

    // Track individual petition page views — most important user action
    const petitionMatch = currentPath.match(/^\/petition\/(\d+)/);
    if (petitionMatch && window.trackAnalytics) {
      window.trackAnalytics('petition_page_view', {
        petition_id: petitionMatch[1],
        referrer: document.referrer.slice(0, 100),
        device_type: window.innerWidth < 768 ? 'mobile' : window.innerWidth < 1024 ? 'tablet' : 'desktop'
      });
    }

    // Track page exit with time spent and scroll depth on route change
    return () => {
      const timeSpent = Math.floor((Date.now() - pageStartTime.current) / 1000);
      if (window.trackAnalytics && timeSpent > 2) { // skip bounces under 2s
        window.trackAnalytics('page_exit', {
          page: currentPath,
          time_spent: timeSpent,
          max_scroll_depth: maxScrollDepthRef.current,
          total_clicks: clickCountRef.current
        });
      }
      if (scrollTimer.current) clearTimeout(scrollTimer.current);
    };
  }, [location.pathname]);

  // Track scroll depth milestones (25%, 50%, 75%, 100%)
  useEffect(() => {
    const handleScroll = () => {
      const scrollPercent = Math.round(
        ((window.pageYOffset + window.innerHeight) / document.documentElement.scrollHeight) * 100
      );
      maxScrollDepthRef.current = Math.max(maxScrollDepthRef.current, Math.min(scrollPercent, 100));

      if (scrollTimer.current) clearTimeout(scrollTimer.current);
      scrollTimer.current = setTimeout(() => {
        const milestones = [25, 50, 75, 100];
        const milestone = milestones.find(m =>
          maxScrollDepthRef.current >= m &&
          !sessionStorage.getItem(`scroll_${location.pathname}_${m}`)
        );
        if (milestone && window.trackAnalytics) {
          window.trackAnalytics('scroll_milestone', {
            page: location.pathname,
            depth: milestone,
            time_to_scroll: Math.floor((Date.now() - pageStartTime.current) / 1000)
          });
          sessionStorage.setItem(`scroll_${location.pathname}_${milestone}`, 'true');
        }
      }, 500);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (scrollTimer.current) clearTimeout(scrollTimer.current);
    };
  }, [location.pathname]);

  // Track external link and button clicks
  useEffect(() => {
    const handleClick = (e) => {
      clickCountRef.current++;
      const target = e.target;
      const tagName = target.tagName.toLowerCase();

      if (tagName === 'a') {
        const href = target.href;
        const isExternal = href && !href.includes(window.location.hostname);
        if (isExternal && window.trackAnalytics) {
          window.trackAnalytics('external_link_click', {
            destination: href.slice(0, 100),
            link_text: target.textContent?.trim().slice(0, 50) || '',
            page: location.pathname
          });
        }
      } else if (tagName === 'button' || target.type === 'submit') {
        const buttonText = target.textContent?.trim().slice(0, 50);
        // Only track meaningful buttons — skip generic nav/UI buttons
        const skipPatterns = /^(×|close|dismiss|✕|▶|◀|›|‹|next|prev)$/i;
        if (buttonText && !skipPatterns.test(buttonText) && window.trackAnalytics) {
          window.trackAnalytics('button_click', {
            button_text: buttonText,
            page: location.pathname
          });
        }
      }
    };

    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, [location.pathname]);

  return null;
};

// ============ CANONICAL URL MANAGER COMPONENT ============
const CanonicalManager = () => {
  const location = useLocation();

  useEffect(() => {
    const baseUrl = 'https://ukpetitiontracker.com';
    let path = location.pathname;

    if (path !== '/' && path.endsWith('/')) {
      path = path.slice(0, -1);
    }

    const canonicalUrl = `${baseUrl}${path}`;

    let canonicalTag = document.querySelector('link[rel="canonical"]');
    if (!canonicalTag) {
      canonicalTag = document.createElement('link');
      canonicalTag.rel = 'canonical';
      document.head.appendChild(canonicalTag);
    }
    canonicalTag.href = canonicalUrl;

    let ogUrlTag = document.querySelector('meta[property="og:url"]');
    if (!ogUrlTag) {
      ogUrlTag = document.createElement('meta');
      ogUrlTag.setAttribute('property', 'og:url');
      document.head.appendChild(ogUrlTag);
    }
    ogUrlTag.content = canonicalUrl;

    let twitterUrlTag = document.querySelector('meta[name="twitter:url"]');
    if (!twitterUrlTag) {
      twitterUrlTag = document.createElement('meta');
      twitterUrlTag.setAttribute('name', 'twitter:url');
      document.head.appendChild(twitterUrlTag);
    }
    twitterUrlTag.content = canonicalUrl;

    const hreflangTags = document.querySelectorAll('link[hreflang]');
    hreflangTags.forEach(tag => {
      if (tag.getAttribute('hreflang') === 'en-GB') {
        tag.href = canonicalUrl;
      }
    });

  }, [location.pathname]);

  return null;
};

// ============ REDIRECT HANDLER COMPONENT ============
const RedirectHandler = () => {
  const location = useLocation();

  useEffect(() => {
    const path = location.pathname;

    if (path !== '/' && path.endsWith('/')) {
      const newPath = path.slice(0, -1);
      window.history.replaceState(null, '', newPath + location.search + location.hash);
    }
  }, [location]);

  return null;
};

// ============ META TAGS UPDATER COMPONENT ============
const MetaTagsUpdater = () => {
  const location = useLocation();

  useEffect(() => {
    const path = location.pathname;
    let title = 'UK Petition Tracker | Live Parliament & Government Petitions Analytics';
    let description = 'Track UK Parliament and Government petitions. Live signature counts, growth trends, constituency breakdowns, debate dates and government responses. Free.';

    if (path.startsWith('/petition/')) {
      title = 'Petition Details | UK Petition Tracker';
      description = 'View detailed analytics and real-time signature tracking for this UK Parliament petition.';
    } else if (path === '/petitions/open') {
      title = 'Open Petitions | UK Petition Tracker';
      description = 'Browse all currently open UK Parliament petitions. Track signatures, government responses, and debates in real-time.';
    } else if (path === '/petitions/closed') {
      title = 'Closed Petitions | UK Petition Tracker';
      description = 'Explore closed UK Parliament petitions with historical data, government responses, and debate outcomes.';
    } else if (path === '/petitions/trending') {
      title = 'Trending Petitions | UK Petition Tracker';
      description = 'Discover rapidly growing UK Parliament petitions with real-time signature tracking and trend analysis.';
    } else if (path === '/petitions/rejected') {
      title = 'Rejected Petitions | UK Petition Tracker';
      description = 'View UK Parliament petitions that were rejected with reasons for rejection and related information.';
    } else if (path.startsWith('/petitions/local/')) {
      const slug = path.replace('/petitions/local/', '');
      const name = slug.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
      title = `${name} Local Petitions | UK Petition Tracker`;
      description = `Find UK Parliament petitions in ${name}. See what your constituency is signing and contact your MP.`;
    } else if (path === '/petitions/local') {
      title = 'Local Petitions | UK Petition Tracker';
      description = 'Find UK Parliament petitions relevant to your local area with constituency-based filtering.';
    } else if (path === '/petitions/latest') {
      title = 'Latest Petitions | UK Petition Tracker';
      description = 'View the most recently submitted UK Parliament petitions. Discover new causes and issues as they are published.';
    } else if (path === '/petitions/awaiting-response') {
      title = 'Awaiting Government Response | UK Petition Tracker';
      description = 'UK Parliament petitions that have passed 10,000 signatures and are awaiting an official government response. Updated in real-time.';
    } else if (path === '/petitions/debate-scheduled') {
      title = 'Debate Scheduled | UK Petition Tracker';
      description = 'UK Parliament petitions with a confirmed debate date. See which petitions are being debated in Parliament and when.';
    } else if (path === '/insights' || path.startsWith('/insights/')) {
      title = 'Insights | UK Petition Tracker';
      description = 'Data-driven analysis and research on UK Parliament petitions — signature trends, regional breakdowns, government responses, and political patterns.';
    } else if (path === '/about') {
      title = 'About | UK Petition Tracker';
      description = 'Learn about UK Petition Tracker — the free real-time analytics platform for UK Parliament petition tracking and analysis.';
    } else if (path === '/faq') {
      title = 'FAQ — Frequently Asked Questions | UK Petition Tracker';
      description = 'Find answers about UK Petition Tracker. Learn how to track Parliament petitions, understand signature data, government responses, and debates.';
    }

    document.title = title;

    const metaTitleTag = document.querySelector('meta[name="title"]');
    if (metaTitleTag) metaTitleTag.content = title;

    const metaDescTag = document.querySelector('meta[name="description"]');
    if (metaDescTag) metaDescTag.content = description;

    const ogTitleTag = document.querySelector('meta[property="og:title"]');
    if (ogTitleTag) ogTitleTag.content = title;

    const ogDescTag = document.querySelector('meta[property="og:description"]');
    if (ogDescTag) ogDescTag.content = description;

    const twitterTitleTag = document.querySelector('meta[name="twitter:title"]');
    if (twitterTitleTag) twitterTitleTag.content = title;

    const twitterDescTag = document.querySelector('meta[name="twitter:description"]');
    if (twitterDescTag) twitterDescTag.content = description;

  }, [location.pathname]);

  return null;
};

// Home Page Component with tracking
const HomeRoute = ({ onAnalyze, loading, error }) => {
  useEffect(() => {
    // Track homepage specific events
    if (window.trackAnalytics) {
      window.trackAnalytics('homepage_loaded', {
        has_error: !!error,
        is_loading: loading
      });
    }
  }, [error, loading]);

  return (
    <>
      {loading && <LoadingSpinner message="Fetching petition data..." />}
      {error && <ErrorMessage message={error} />}
      {!loading && <HomePage onAnalyze={onAnalyze} />}
    </>
  );
};

function App() {
  const [notifications, setNotifications] = useState([]);

  // Track app initialization (language/device only — GA4 captures user agent natively)
  useEffect(() => {
    if (window.trackAnalytics) {
      window.trackAnalytics('app_initialized', {
        language: navigator.language,
        device_type: window.innerWidth < 768 ? 'mobile' : window.innerWidth < 1024 ? 'tablet' : 'desktop'
      });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const addNotification = useCallback((notification) => {
    const id = Date.now();
    const newNotification = { id, ...notification };
    setNotifications(prev => [...prev, newNotification]);

    // Track notification shown
    if (window.trackAnalytics) {
      window.trackAnalytics('notification_shown', {
        type: notification.type,
        message_preview: notification.message.slice(0, 30)
      });
    }

    setTimeout(() => {
      setNotifications(prev => prev.filter(n => n.id !== id));
    }, 4000);
  }, []);

  const handleAnalyzePetition = useCallback(async (url) => {
    const startTime = Date.now();

    try {
      const match = url.match(/\/petitions\/(\d+)/);
      if (!match) {
        throw new Error('Invalid petition URL format');
      }

      const petitionId = match[1];

      if (window.trackAnalytics) {
        window.trackAnalytics('petition_analyze_started', {
          petition_id: petitionId,
          source: 'url_input',
          url_format: url.includes('petition.parliament.uk') ? 'official' : 'custom'
        });
      }

      window.location.href = `/petition/${petitionId}`;

    } catch (err) {
      console.error('Navigation error:', err);

      if (window.trackAnalytics) {
        window.trackAnalytics('petition_analyze_error', {
          error: err.message,
          url: url,
          time_elapsed: Date.now() - startTime
        });
      }

      addNotification({
        type: 'error',
        message: `Error: ${err.message}`
      });
    }
  }, [addNotification]);

  const handleUpdateNotification = useCallback((updateInfo) => {
    if (updateInfo.hasUpdate && updateInfo.difference > 0) {
      addNotification({
        type: 'update',
        message: `+${updateInfo.difference.toLocaleString()} new signature${updateInfo.difference === 1 ? '' : 's'}`
      });

      if (window.trackAnalytics) {
        window.trackAnalytics('signature_update_notification', {
          petition_id: updateInfo.data?.id,
          new_signatures: updateInfo.difference,
          new_count: updateInfo.newCount,
          percentage_change: updateInfo.oldCount ?
            ((updateInfo.difference / updateInfo.oldCount) * 100).toFixed(2) : 0
        });
      }

      // Track milestone if reached
      const milestones = [100, 500, 1000, 5000, 10000, 50000, 100000];
      const milestone = milestones.find(m =>
        updateInfo.newCount >= m &&
        updateInfo.oldCount < m
      );

      if (milestone && window.trackAnalytics) {
        window.trackAnalytics('petition_milestone_reached', {
          petition_id: updateInfo.data?.id,
          milestone: milestone,
          signatures: updateInfo.newCount
        });
      }
    }
  }, [addNotification]);

  const handleNotificationClick = useCallback((notificationId) => {
    if (window.trackAnalytics) {
      window.trackAnalytics('notification_dismissed', {
        notification_id: notificationId,
        method: 'click'
      });
    }

    setNotifications(prev => prev.filter(n => n.id !== notificationId));
  }, []);

  return (
    <Router>
      <AppContainer>
        {/* SEO and Analytics Components */}
        <CanonicalManager />
        <RedirectHandler />
        <MetaTagsUpdater />
        <AnalyticsRouteTracker />
        <LiveDebateBar />
        <EnhancedAnalyticsTracker /> {/* New enhanced tracking component */}

        <Container>
          <ScrollToTop />
          <Suspense fallback={<LoadingSpinner />}>
          <Routes>
            <Route
              path="/"
              element={
                <HomeRoute
                  onAnalyze={handleAnalyzePetition}
                  loading={false}
                  error={null}
                />
              }
            />

            <Route path="/petitions/open" element={<OpenPetitionsPage />} />
            <Route path="/petitions/latest" element={<LatestPetitionsPage />} />
            <Route path="/petitions/local" element={<LocalPetitionsPage />} />
            <Route path="/petitions/local/:constituency" element={<LocalPetitionsPage />} />
            <Route path="/petitions/closed" element={<ClosedPetitionsPage />} />
            <Route path="/petitions/rejected" element={<RejectedPetitionsPage />} />
            <Route path="/compare" element={<ComparePage />} />
            <Route path="/petitions/trending" element={<TrendingPetitionsPage />} />
            <Route path="/insights" element={<InsightsPage />} />
            <Route path="/insights/:slug" element={<InsightArticlePage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/faq" element={<FAQPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/whats-new" element={<ChangelogPage />} />
            <Route path="/features" element={<FeaturesPage />} />
            <Route path="/statistics" element={<StatsPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/faqs" element={<FAQPage />} />

            {/* Petition route aliases */}
            <Route path="/petitions/all" element={<OpenPetitionsPage />} />
            <Route path="/petitions/recently-opened" element={<LatestPetitionsPage />} />
            <Route path="/petitions/recently-closed" element={<ClosedPetitionsPage />} />
            <Route path="/petitions/recently-rejected" element={<RejectedPetitionsPage />} />
            <Route path="/petitions/awaiting-response" element={<AwaitingResponsePage />} />
            <Route path="/petitions/debate-scheduled" element={<DebateScheduledPage />} />
            <Route path="/petitions/milestones" element={<NearMilestonesPage />} />
            <Route path="/petitions/government-responded" element={<ClosedPetitionsPage />} />
            <Route path="/petitions/debated" element={<ClosedPetitionsPage />} />

            <Route path="/petition" element={<Navigate to="/petitions/open" replace />} />

            <Route
              path="/petition/:petitionId"
              element={
                <PetitionPage
                  onUpdate={handleUpdateNotification}
                />
              }
            />
            <Route
              path="/petition/:petitionId/:slug"
              element={
                <PetitionPage
                  onUpdate={handleUpdateNotification}
                />
              }
            />

            <Route
              path="*"
              element={<NotFoundPage />}
            />
          </Routes>
          </Suspense>
        </Container>

        <NotificationContainer>
          {notifications.map(notification => (
            <Notification
              key={notification.id}
              onClick={() => handleNotificationClick(notification.id)}
            >
              {notification.message}
            </Notification>
          ))}
        </NotificationContainer>

        {/* Global footer — rendered once here for every route. Page
            components must NOT render their own <Footer />, or the page
            ends up with two. */}
        <Footer />
        <CookieConsent />
        {/* Auto-prompt disabled for now. The footer and the in-page support
            asks still link to Buy Me a Coffee; this was the 30s modal.
            Uncomment to bring it back. */}
        {/* <SupportModal /> */}
      </AppContainer>
    </Router>
  );
}

export default App;